import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  InAppDialogRequest,
  installInAppAlertOverride,
  showInAppAlert,
  subscribeInAppDialogs
} from '../services/inAppDialog';

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(18, 63, 67, 0.38)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 4000
  },
  modal: {
    width: '92%',
    maxWidth: 540,
    backgroundColor: '#FBF8F2',
    borderRadius: 16,
    border: '1px solid #DDD4C8',
    boxShadow: '0 18px 48px rgba(18, 63, 67, 0.18)',
    padding: '1.1rem 1.15rem',
    display: 'grid',
    gap: '0.75rem'
  },
  title: {
    margin: 0,
    fontSize: '1.06rem',
    color: '#123F43'
  },
  message: {
    margin: 0,
    color: '#4B6470',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap'
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem'
  },
  neutralButton: {
    border: '1px solid #DDD4C8',
    backgroundColor: '#FFFDF9',
    color: '#123F43',
    borderRadius: 999,
    padding: '0.42rem 0.95rem',
    fontWeight: 600,
    cursor: 'pointer'
  },
  primaryButton: {
    border: '1px solid #123F43',
    backgroundColor: '#123F43',
    color: '#ffffff',
    borderRadius: 999,
    padding: '0.42rem 0.95rem',
    fontWeight: 600,
    cursor: 'pointer'
  },
  dangerButton: {
    border: 'none',
    backgroundColor: '#dc2626',
    color: '#ffffff',
    borderRadius: 999,
    padding: '0.42rem 0.95rem',
    fontWeight: 600,
    cursor: 'pointer'
  }
};

const InAppDialogHost: React.FC = () => {
  const [queue, setQueue] = useState<InAppDialogRequest[]>([]);
  const queueRef = useRef<InAppDialogRequest[]>([]);

  useEffect(() => {
    queueRef.current = queue;
  }, [queue]);

  useEffect(() => {
    installInAppAlertOverride();
    const unsubscribe = subscribeInAppDialogs(request => {
      setQueue(prev => [...prev, request]);
    });
    return () => {
      const pending = queueRef.current;
      pending.forEach(request => {
        if (request.mode === 'confirm' && request.resolve) {
          request.resolve(false);
        }
      });
      queueRef.current = [];
      unsubscribe();
    };
  }, []);

  const current = queue[0] || null;

  const closeDialog = useCallback((confirmed: boolean) => {
    setQueue(prev => {
      if (prev.length === 0) return prev;
      const [first, ...rest] = prev;
      if (first.mode === 'confirm' && first.resolve) {
        first.resolve(confirmed);
      }
      return rest;
    });
  }, []);

  useEffect(() => {
    if (!current) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      closeDialog(current.mode === 'alert');
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [closeDialog, current]);

  useEffect(() => {
    if (!current?.message) return;
    const trimmed = current.message.trim();
    if (trimmed.length > 0) return;
    closeDialog(current.mode === 'alert');
    showInAppAlert('An empty dialog message was suppressed.', { title: 'Notice' });
  }, [closeDialog, current]);

  if (!current) return null;

  const confirmButtonStyle =
    current.tone === 'danger' ? styles.dangerButton : styles.primaryButton;

  return (
    <div style={styles.backdrop} onClick={() => closeDialog(current.mode === 'alert')}>
      <div style={styles.modal} onClick={event => event.stopPropagation()}>
        <h3 style={styles.title}>{current.title}</h3>
        <p style={styles.message}>{current.message}</p>
        <div style={styles.buttonRow}>
          {current.mode === 'confirm' && (
            <button
              type="button"
              style={styles.neutralButton}
              onClick={() => closeDialog(false)}
            >
              {current.cancelLabel || 'Cancel'}
            </button>
          )}
          <button
            type="button"
            style={confirmButtonStyle}
            onClick={() => closeDialog(true)}
          >
            {current.confirmLabel || 'OK'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InAppDialogHost;
